import React, { FC, memo } from 'react'
import Costs from './costs'
import SearchbarDatePicker from '../../search-result-listing/header-section/searchbar/date-picker'
import SearchbarGuestPicker from '../../search-result-listing/header-section/searchbar/guest-picker'
import { DatePickerAlignment } from '../../common/date-picker/types'
import { PriceInputWrapper, PriceInput } from './styles'
import TitleBar from './titlebar'
import { ComponentSeparator, DottedSeparator, ExposeSectionTitleWrapper } from '../style'
import { Typography } from 'react-components'
import { useTranslation } from '../../../hooks/locale'
import BookNowButton from '../book-now-button'
import EnquiryButton from '../enquiry-button'
import Notes from './notes'
import { FormattedTravelNote } from '../../../models/property-details-page/notes-model'
import { FormattedBookingCancellationPolicy } from '../../../models/property-details-page/booking-cancellation-policy-model'
import { SearchbarFieldsReturn, UpdateSearchBarTransport } from '../hooks/use-searchbar'
import get from 'lodash/get'
import InclusiveCosts from './inclusive-costs'
import OptionalCosts from './optional-costs'
import UsageBoundCosts from './usage-bound-costs'
import { FormattedBookingTypeData } from '../../../models/property-details-page/booking-type-model'
import isInquiryButtonVisible from '../side-bar/utils/is-inquiry-button-visible'
import isDirectBookingButtonVisible from '../side-bar/utils/is-direct-booking-button-visible'
import is24HourBookingButtonVisible from '../side-bar/utils/is-24-hour-booking-button-visible'
import BindingInquiryButton from '../binding-inquiry-button'
import Offers from './offers'
import { FormattedOfferTransport } from '../../../models/property-details-page/offers-model'
import { FormattedLivePricingDataTransport } from '../../../models/pricing/live-pricing-formatter'
import { PdpToCheckoutPosition, SECTION_IDS } from '../constants'
import isNumber from 'lodash/isNumber'
import InquiryContactDetailsTransport from '../../../transports/inquiry-contact-details-transport'
import useDateGuestPicker from '../hooks/use-date-guest-picker'
import UserDetailsModel from '../../../models/user/user-details-model'

interface PriceProps {
    layout: 'compact' | 'stretched'
    datePickerAlignment?: DatePickerAlignment
    showPriceTitle?: boolean
    position?: PdpToCheckoutPosition.SIDEBAR | PdpToCheckoutPosition.CONTENT
    notes?: FormattedTravelNote[]
    bookingCancellationPolicy?: FormattedBookingCancellationPolicy | null
    searchbar: SearchbarFieldsReturn
    updateSearchbar: UpdateSearchBarTransport
    availableDates: Date[] | null
    isAvailabilityLoading: boolean
    pricingDetails: FormattedLivePricingDataTransport | null
    isPricingLoading: boolean
    pricingErrorCode: number | null
    bookingType: FormattedBookingTypeData | null
    offers: FormattedOfferTransport | null
    showVatTax: boolean
    isTraumPaymentEnabled: boolean
    showCalendar: boolean
    hideExtraInfo?: boolean
    maxTotalGuests?: number
    inquiryContactDetails: InquiryContactDetailsTransport | null
    userDetails: UserDetailsModel | null
    petsAllowed: boolean
}

const Price: FC<PriceProps> = ({
    layout = 'compact',
    datePickerAlignment = DatePickerAlignment.RIGHT,
    showPriceTitle = false,
    position = PdpToCheckoutPosition.CONTENT,
    notes,
    bookingCancellationPolicy,
    searchbar,
    updateSearchbar,
    availableDates,
    isAvailabilityLoading,
    pricingDetails,
    pricingErrorCode,
    isPricingLoading,
    bookingType,
    offers,
    showVatTax,
    isTraumPaymentEnabled,
    showCalendar,
    hideExtraInfo = false,
    maxTotalGuests,
    inquiryContactDetails,
    userDetails,
    petsAllowed,
}) => {
    const {
        isDatePickerOpen,
        isPersonSelectorOpen,
        openDateSelector,
        closeDateSelector,
        openPersonSelector,
        closePersonSelector,
        handleArrivalDateChange,
        handleDepartureDateChange,
        handleAdultsChange,
        handleKidsChange,
        handlePetsChange,
        adultsCount,
        autoCorrectionRef,
        trackArrivalDateChange,
        trackDepartureDateChange,
    } = useDateGuestPicker({
        searchbar,
        updateSearchbar,
        pricingDetails,
        position,
    })

    const { t } = useTranslation()
    const inclusiveCosts = get(pricingDetails, 'inclusiveCosts', [])
    const optionalCosts = get(pricingDetails, 'optionalCosts', [])
    const usageBoundCosts = get(pricingDetails, 'usageBoundCosts', [])
    const specialOffer = get(pricingDetails, 'specialOffer', null)

    return (
        <section
            id={
                position === PdpToCheckoutPosition.CONTENT
                    ? SECTION_IDS.PRICE_SECTION_ID
                    : SECTION_IDS.SIDEBAR_SECTION_ID
            }
        >
            {showPriceTitle && (
                <ExposeSectionTitleWrapper>
                    <Typography variant="h6" fontWeight="semiBold" element="h2">
                        {t('price.priceSectionTitle')}
                    </Typography>
                </ExposeSectionTitleWrapper>
            )}
            {!pricingDetails && !isAvailabilityLoading && (
                <TitleBar pricingErrorCode={pricingErrorCode} searchbar={searchbar} />
            )}
            {(isPricingLoading || pricingDetails || isAvailabilityLoading) && (
                <Costs
                    searchbar={searchbar}
                    pricingDetails={pricingDetails}
                    pricingErrorCode={pricingErrorCode}
                    isPricingLoading={isPricingLoading}
                    isAvailabilityLoading={isAvailabilityLoading}
                    showVatTax={showVatTax}
                    showCalendar={showCalendar}
                />
            )}
            <Offers searchbar={searchbar} offer={offers} specialOffer={specialOffer} />
            <PriceInputWrapper layout={layout}>
                <PriceInput layout={layout}>
                    <SearchbarDatePicker
                        isOpen={isDatePickerOpen}
                        onOpen={openDateSelector}
                        onClose={closeDateSelector}
                        startDate={searchbar.arrival}
                        endDate={searchbar.departure}
                        showInputInModal
                        onStartDateChange={handleArrivalDateChange}
                        onEndDateChange={handleDepartureDateChange}
                        alignmentPosition={datePickerAlignment}
                        enabledDates={availableDates ? availableDates : undefined}
                        trackStartDateChange={trackArrivalDateChange}
                        trackEndDateChange={trackDepartureDateChange}
                        disabledByDefault
                        loading={isAvailabilityLoading}
                        isRounded={'0.5rem'}
                    />
                </PriceInput>

                <PriceInput layout={layout}>
                    <SearchbarGuestPicker
                        isOpen={isPersonSelectorOpen}
                        onOpen={openPersonSelector}
                        onClose={closePersonSelector}
                        adultsCount={adultsCount}
                        childrenCount={searchbar.kids}
                        petsCount={searchbar.pets}
                        showInputInModal
                        onAdultsCountChange={handleAdultsChange}
                        onChildrenCountChange={handleKidsChange}
                        onPetsCountChange={handlePetsChange}
                        minAdults={autoCorrectionRef.current}
                        maxAdults={maxTotalGuests}
                        maxTotalGuests={maxTotalGuests}
                        petsAllowed={petsAllowed}
                        isRounded={'0.5rem'}
                    />
                </PriceInput>
                {isNumber(pricingDetails?.autoCorrection?.adults) && (
                    <Typography variant="caption" fontWeight="regular" color="tfwNegativeMedium">
                        {t(`livePriceErrors.66`, { guests: pricingDetails?.autoCorrection?.adults })}
                    </Typography>
                )}
            </PriceInputWrapper>

            <PriceInputWrapper layout={layout}>
                {isInquiryButtonVisible({ bookingType }) && (
                    <PriceInput layout={layout}>
                        <EnquiryButton
                            bookingType={bookingType}
                            searchbar={searchbar}
                            position={
                                position === PdpToCheckoutPosition.CONTENT
                                    ? PdpToCheckoutPosition.PRICE_WIDGET
                                    : PdpToCheckoutPosition.SIDEBAR
                            }
                            inquiryContactDetails={inquiryContactDetails}
                            pricingDetails={pricingDetails}
                            pricingErrorCode={pricingErrorCode}
                            isPricingLoading={isPricingLoading}
                            isAvailabilityLoading={isAvailabilityLoading}
                            updateSearchbar={updateSearchbar}
                            availableDates={availableDates}
                            userDetails={userDetails}
                            petsAllowed={petsAllowed}
                        />
                    </PriceInput>
                )}
                {isDirectBookingButtonVisible({ bookingType }) && (
                    <PriceInput layout={layout}>
                        <BookNowButton
                            bookingType={bookingType}
                            searchbar={searchbar}
                            position={
                                position === PdpToCheckoutPosition.CONTENT
                                    ? PdpToCheckoutPosition.PRICE_WIDGET
                                    : PdpToCheckoutPosition.SIDEBAR
                            }
                        />
                    </PriceInput>
                )}
                {is24HourBookingButtonVisible({ bookingType }) && (
                    <PriceInput layout={layout}>
                        <BindingInquiryButton
                            bookingType={bookingType}
                            searchbar={searchbar}
                            position={
                                position === PdpToCheckoutPosition.CONTENT
                                    ? PdpToCheckoutPosition.PRICE_WIDGET
                                    : PdpToCheckoutPosition.SIDEBAR
                            }
                        />
                    </PriceInput>
                )}
            </PriceInputWrapper>
            {position === PdpToCheckoutPosition.CONTENT && !hideExtraInfo && (
                <>
                    <UsageBoundCosts usageBoundCosts={usageBoundCosts} />
                    <InclusiveCosts inclusiveCosts={inclusiveCosts} />
                    <OptionalCosts optionalCosts={optionalCosts} />
                    <DottedSeparator />
                    <Notes
                        notes={notes}
                        bookingCancellationPolicy={bookingCancellationPolicy}
                        arrivalDate={searchbar.arrival}
                    />
                    <ComponentSeparator />
                </>
            )}
            {hideExtraInfo && <ComponentSeparator />}
        </section>
    )
}

export default memo(Price)
